import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Compass, Menu, X, LayoutDashboard, MapPin, User, LogOut, Shield, Wallet, ListChecks, Globe } from "lucide-react";
import InstallPWA from "./InstallPWA";
import NotificationBell from "./NotificationBell";

const LangToggle = ({ lang, toggleLang }) => (
  <button
    onClick={toggleLang}
    className="text-xs px-2 py-1 border border-[#DCD4C6] text-[#9A8F86] hover:border-[#C17F59] hover:text-[#C17F59] transition-all"
    style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px", letterSpacing: "0.06em", minWidth: "30px" }}
    data-testid="lang-toggle-btn"
  >
    {lang === "fr" ? "EN" : "FR"}
  </button>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, t, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav
      className="sticky top-0 z-50 border-b border-[#DCD4C6]"
      style={{ backdropFilter: "blur(16px)", background: "rgba(249,246,240,0.88)" }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 600, fontSize: "1.5rem", color: "#2B2623", letterSpacing: "0.04em", textDecoration: "none" }}
            data-testid="nav-logo"
          >
            <div className="w-8 h-8 rounded-full border-2 border-[#C17F59] flex items-center justify-center">
              <Compass size={16} color="#C17F59" strokeWidth={1.5} />
            </div>
            Voyage-r
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {user ? (
              <>
                <Link to="/about" className="px-4 py-2 text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif", letterSpacing: "0.02em" }} data-testid="nav-about">{t("nav.about")}</Link>
                <Link to="/dashboard" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-dashboard">
                  <LayoutDashboard size={14} strokeWidth={1.5} />{t("nav.trips")}
                </Link>
                <Link to="/budget" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-budget">
                  <Wallet size={14} strokeWidth={1.5} />Budget
                </Link>
                <Link to="/checklist" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-checklist">
                  <ListChecks size={14} strokeWidth={1.5} />Ma Valise
                </Link>
                <Link to="/culture" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-culture">
                  <Globe size={14} strokeWidth={1.5} />Guide Local
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#8B5E34] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-admin">
                    <Shield size={14} strokeWidth={1.5} />{t("nav.admin")}
                  </Link>
                )}
                <Link to="/plan" className="btn-primary text-sm ml-2 flex items-center gap-2" data-testid="nav-plan">
                  <MapPin size={14} strokeWidth={1.5} />{t("nav.plan")}
                </Link>
                <InstallPWA />
                <NotificationBell />
                <LangToggle lang={lang} toggleLang={toggleLang} />
                <div className="relative ml-2">
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center gap-2 px-3 py-1.5 border border-[#DCD4C6] rounded-sm hover:border-[#8B5E34] transition-colors" style={{ background: "transparent" }} data-testid="nav-user-menu-btn">
                    <div className="w-6 h-6 bg-[#C17F59] rounded-full flex items-center justify-center text-[#F9F6F0] text-xs font-semibold">{user.name?.[0]?.toUpperCase() || "V"}</div>
                    <span className="text-sm text-[#2B2623] max-w-[100px] truncate" style={{ fontFamily: "'Lora', Georgia, serif" }}>{user.name}</span>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-[#F1EBE1] border border-[#DCD4C6] rounded-sm shadow-lg py-1 animate-fade-in">
                      <Link to="/account" className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#2B2623] hover:bg-[#F9F6F0]" style={{ fontFamily: "'Lora', Georgia, serif" }} onClick={() => setUserMenuOpen(false)} data-testid="nav-account-link">
                        <User size={14} strokeWidth={1.5} />{t("nav.account")}
                      </Link>
                      <button onClick={handleLogout} className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-logout-btn">
                        <LogOut size={14} strokeWidth={1.5} />{t("nav.logout")}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/about" className="px-4 py-2 text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-about">{t("nav.about")}</Link>
                <Link to="/login" className="px-4 py-2 text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="nav-login">{t("nav.login")}</Link>
                <InstallPWA />
                <LangToggle lang={lang} toggleLang={toggleLang} />
                <Link to="/register" className="btn-primary text-sm ml-2" data-testid="nav-register">{t("nav.register")}</Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden p-2 rounded-sm text-[#5C554E] hover:text-[#2B2623]" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-mobile-menu-btn">
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden pb-4 border-t border-[#DCD4C6] mt-1 animate-fade-in">
            {user ? (
              <div className="pt-3 flex flex-col gap-1">
                <Link to="/about" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#5C554E] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-about">{t("nav.about_diego")}</Link>
                <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#5C554E] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-dashboard"><LayoutDashboard size={15} strokeWidth={1.5} />{t("nav.trips")}</Link>
                <Link to="/budget" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#5C554E] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-budget"><Wallet size={15} strokeWidth={1.5} />Budget voyage</Link>
                <Link to="/checklist" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#5C554E] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-checklist"><ListChecks size={15} strokeWidth={1.5} />Ma Valise</Link>
                <Link to="/culture" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#5C554E] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-culture"><Globe size={15} strokeWidth={1.5} />Guide Local</Link>
                <Link to="/plan" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#C17F59] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-plan"><MapPin size={15} strokeWidth={1.5} />{t("nav.new_trip")}</Link>
                {user.role === "admin" && <Link to="/admin" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#8B5E34] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-admin"><Shield size={15} strokeWidth={1.5} />{t("nav.admin")}</Link>}
                <Link to="/account" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#5C554E] hover:bg-[#F1EBE1]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-account"><User size={15} strokeWidth={1.5} />{t("nav.account")}</Link>
                <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-logout"><LogOut size={15} strokeWidth={1.5} />{t("nav.logout")}</button>
                <div className="px-3 pt-2 pb-1 flex items-center gap-2">
                  <InstallPWA />
                  <LangToggle lang={lang} toggleLang={toggleLang} />
                </div>
              </div>
            ) : (
              <div className="pt-3 flex flex-col gap-2">
                <Link to="/about" className="text-center py-2.5 text-sm text-[#5C554E]" onClick={() => setMenuOpen(false)} style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="mobile-nav-about">{t("nav.about")}</Link>
                <Link to="/login" className="btn-secondary text-center text-sm" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-login">{t("nav.login")}</Link>
                <Link to="/register" className="btn-primary text-center text-sm" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-register">{t("nav.register")}</Link>
                <div className="px-3 pt-2 pb-1 flex items-center gap-2">
                  <InstallPWA />
                  <LangToggle lang={lang} toggleLang={toggleLang} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
